import { useId, useState, type FormEvent } from 'react';
import { DELETE_ACCOUNT_WORD } from '@shared/schemas';
import { del, errorMessage } from '../lib/api';
import { Dialog } from './Dialog';
import { Notice } from './Notice';

interface Props {
  open: boolean;
  hasPassword: boolean;
  hasSubscription: boolean;
  onClose(): void;
  onDeleted(): void;
}

/** Permanent deletion of the signed-in account, confirmed by typing a word and the password. */
export function DeleteAccountDialog({ open, hasPassword, hasSubscription, onClose, onDeleted }: Props) {
  const formId = useId();
  const wordId = useId();
  const passwordId = useId();
  const [word, setWord] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmed = word.trim().toUpperCase() === DELETE_ACCOUNT_WORD && (!hasPassword || password.length > 0);

  function close() {
    if (busy) return;
    setWord('');
    setPassword('');
    setError(null);
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await del('/api/user/account', { confirm: DELETE_ACCOUNT_WORD, ...(hasPassword ? { password } : {}) });
      onDeleted();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Excluir minha conta"
      onClose={close}
      dismissible={!busy}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn btn--danger" disabled={!confirmed || busy}>
            {busy ? 'Excluindo…' : 'Excluir definitivamente'}
          </button>
        </>
      }
    >
      <form id={formId} className="form" onSubmit={submit} noValidate>
        <Notice kind="warning">Esta ação não pode ser desfeita.</Notice>
        <ul className="delete-list">
          <li>A conta, o quadro personalizado (categorias, símbolos e fotos) e as configurações serão apagados.</li>
          {hasSubscription && <li>A assinatura será cancelada e não haverá novas cobranças.</li>}
          <li>Você sairá da conta em todos os aparelhos.</li>
        </ul>
        <div className="field">
          <label htmlFor={wordId}>
            Digite <b>{DELETE_ACCOUNT_WORD}</b> para confirmar
          </label>
          <input id={wordId} autoComplete="off" autoCapitalize="characters" value={word} onChange={(e) => setWord(e.target.value)} />
        </div>
        {hasPassword && (
          <div className="field">
            <label htmlFor={passwordId}>Sua senha</label>
            <input
              id={passwordId}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}
        {error && <Notice kind="error">{error}</Notice>}
      </form>
    </Dialog>
  );
}
